import { useQuery } from '@tanstack/react-query';
import { getVoiceModel, getVoiceModels, getVoiceModelsFeatured } from '@/api/voice';
import { useState, useEffect, useMemo } from 'react';
import { debounce } from '@/utils/helper';

interface UseVoiceModelProp {
	selectedCard: string | null;
	searchText: string;
	provider?: string;
	gender?: string;
	language?: string;
	accent?: string;
	voiceId?: string;
	voiceIdMultilingual?: string;
}

export const useVoiceModels = ({
	selectedCard,
	searchText,
	provider,
	gender,
	language,
	accent,
	voiceId,
	voiceIdMultilingual,
	// lastEvaluatedKeyParam: string | null
}: UseVoiceModelProp) => {
	const [debouncedSearchText, setDebouncedSearchText] = useState<string>(searchText);
	const [page, setPage] = useState(1);
	const [forwarding, setForwarding] = useState<boolean>(true);
	const [lastEvaluatedKey, setLastEvaluatedKey] = useState<string | null>(null);
	const [totalCount, setTotalCount] = useState<number | null>(null);

	useEffect(() => {
		const handler = debounce(() => setDebouncedSearchText(searchText), 300);
		handler();
	}, [searchText]);

	const { data, isPending, refetch } = useQuery({
		queryKey: [
			'voiceModels',
			debouncedSearchText,
			provider,
			gender,
			language,
			accent,
			page,
			selectedCard,
		],
		queryFn: () =>
			getVoiceModels({
				search: debouncedSearchText,
				provider,
				gender,
				language,
				accent,
				page,
				lastEvaluatedKey,
				selectedCard,
				skipTotalCount: totalCount !== null,
				isBackwardIndexing: !forwarding,
			}),
		staleTime: 1000 * 60 * 60,
		refetchOnWindowFocus: false,
		refetchOnMount: false,
	});

	const { data: dataFeatured } = useQuery({
		queryKey: ['voiceModelsFeatured'],
		queryFn: () => getVoiceModelsFeatured(),
		staleTime: 1000 * 60 * 60,
		refetchOnWindowFocus: false,
		refetchOnMount: false,
	});

	const { data: voiceModel, refetch: refetchVoiceModel } = useQuery({
		queryKey: ['voiceModel'],
		queryFn: () => getVoiceModel(voiceId),
		staleTime: 1000 * 60 * 60,
		refetchOnWindowFocus: false,
		refetchOnMount: false,
	});

	const { data: voiceModelMultilingual, refetch: refetchvoiceModelMultilingual } = useQuery({
		queryKey: ['voiceModelMultilingual'],
		queryFn: () => getVoiceModel(voiceIdMultilingual),
		staleTime: 1000 * 60 * 60,
		refetchOnWindowFocus: false,
		refetchOnMount: false,
	});

	useEffect(() => {
		refetch();
	}, [provider, debouncedSearchText, gender, language, accent, refetch, selectedCard]);

	useEffect(() => {
		refetchVoiceModel();
	}, [voiceId, selectedCard]);

	useEffect(() => {
		refetchvoiceModelMultilingual();
	}, [voiceIdMultilingual]);

	useEffect(() => {
		const newKey = data?.data?.lastEvaluatedKey;
		if (newKey && newKey !== lastEvaluatedKey) {
			setLastEvaluatedKey(newKey);
		}
	}, [data?.data?.lastEvaluatedKey]);

	useEffect(() => {
		if (data?.data?.totalCount) {
			setTotalCount(data?.data?.totalCount);
		}
	}, [data?.data?.totalCount]);

	const sortedVoiceModels = useMemo(() => {
		const allVoiceModels = data?.data?.data || [];

		if (page === 1 && selectedCard) {
			const selectedVoice = allVoiceModels.find((v) => v.voiceId === selectedCard);
			const rest = allVoiceModels.filter((v) => v.voiceId !== selectedCard);
			return selectedVoice ? [selectedVoice, ...rest] : rest;
		}

		return allVoiceModels;
	}, [data, selectedCard, page]);

	return {
		voiceModels: sortedVoiceModels,
		voiceModelsFeatured: dataFeatured?.data || [],
		voiceModel: voiceModel?.data,
		voiceModelMultilingual: voiceModelMultilingual?.data,
		isPending,
		refetch,
		setPage,
		page: page || 1,
		limit: data?.data?.limit || 12,
		totalCount: totalCount || 0,
		totalPages: data?.data?.totalPages || 0,
		hasNextPage: data?.data?.hasNextPage || false,
		hasPrevPage: data?.data?.hasPrevPage || false,
		setLastEvaluatedKey,
		setForwarding,
	};
};
